import { useEffect, type ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import Command, { type CommandItemProps } from "@/v2-ui/command"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import { useSelectorContext } from "@/v2-backoffice-client/components/selector/selector.context"

export type SelectorItemProps = CommandItemProps & {
  added?: boolean
  isLoading?: boolean
  onDeselect?(): void
  /**
   * A reason to think twice before picking this item. With it set, a click
   * on the row no longer picks it: the row shows the reason and a confirm
   * button, and only the confirmation runs `onSelect`. For a pick that is
   * allowed but usually a mistake (a membership that crosses customers).
   */
  warning?: ReactNode
  /** The confirm button's question when `warning` is set. */
  confirmContent?: string
}

function SelectorItem(props: SelectorItemProps) {
  const {
    className,
    value,
    added,
    disabled,
    isLoading,
    onSelect,
    onDeselect,
    warning,
    confirmContent = "Continue?",
    children,
    ...commandItemProps
  } = props

  const { setIsLoading } = useSelectorContext()

  useEffect(() => {
    if (isLoading === undefined || !setIsLoading) return
    setIsLoading(isLoading)
  }, [isLoading, setIsLoading])

  const isDeselectable = added && !!onDeselect
  const needsConfirm = Boolean(warning) && !added

  return (
    <Command.Item
      className={cn(
        "selector-item-base",
        added && "selector-item-added",
        needsConfirm && "selector-item-warned",
        className,
      )}
      value={value}
      disabled={(added && !onDeselect) || disabled || isLoading}
      onSelect={added || needsConfirm ? undefined : onSelect}
      {...commandItemProps}
    >
      <div className="selector-item">
        <Icon
          className={cn(
            "selector-item-check",
            needsConfirm && "selector-item-warning-icon",
          )}
          src={added ? "check" : needsConfirm ? "triangle-warning" : undefined}
          size={14}
        />

        <div className="selector-item-body">
          {children}
          {needsConfirm && (
            <span className="selector-item-warning">{warning}</span>
          )}
        </div>

        {(needsConfirm || isDeselectable) && (
          <div
            className="selector-item-remove"
            onClick={(e) => {
              e.stopPropagation()
            }}
            onPointerDown={(e) => {
              e.stopPropagation()
            }}
          >
            {needsConfirm ? (
              <ConfirmButton
                size="xs"
                variant="warning"
                iconSrc="check"
                content={confirmContent}
                confirmContent={confirmContent}
                confirmVariant="warning"
                isLoading={isLoading}
                onConfirm={() => {
                  onSelect?.(value ?? "")
                }}
              />
            ) : (
              <ConfirmButton
                size="xs"
                variant="secondary"
                iconSrc="cross-small"
                content="Clear selection"
                confirmContent="Remove?"
                confirmVariant="destructive"
                onConfirm={() => {
                  onDeselect?.()
                }}
              />
            )}
          </div>
        )}
      </div>
    </Command.Item>
  )
}

export default SelectorItem
